import { FieldError, SubmitHandler, useForm } from "react-hook-form";
import { Fragment, useEffect, useState } from "react";
import {
    SelectedFilterInterface,
    VacancyAlertProps
} from "storyblok/components";
import Image from "next/legacy/image";
import ReactDOMServer from "react-dom/server";
import useTranslation from "next-translate/useTranslation";

import { Button, CHECK, CLOSE, Icon, Input, MAIL } from "components/ui";
import {
    SuccessBlock,
    SuccessIcon,
    SuccessTitle
} from "components/contactForm/ContactForm.styled";
import { TextArea } from "components/ui/textArea";

import {
    Container,
    FilterOption,
    FilterOptionsWrapper,
    Form,
    ImageWrapper,
    InputWrapper,
    NameAndEmailWrapper,
    StyledErrorMessage,
    Wrapper
} from "./VacancyAlert.styled";

import { filterOptionslabels } from "helpers/util/filterOptionsLabels";
import { getEducationCode } from "helpers/hooks/getEducationCode";
import { useFilterOptions } from "helpers/hooks/useFilterOptions";
import { useMediaQueryContext } from "helpers/hooks";
import filterTupleFilterer from "helpers/util/filterTupleFilterer";
import filterTupleMaker from "helpers/util/filterTupleMaker";

interface FormInputFields {
    email: string;
    fullName: string;
    comment: string;
}

export function VacancyAlert({ blok }: VacancyAlertProps): JSX.Element {
    const { t } = useTranslation();
    const { title, text, image, buttonText } = blok;
    const { isTabletViewOrLarger } = useMediaQueryContext();
    const { filterOptions } = useFilterOptions();
    const [successful, setSuccessful] = useState(false);
    const [departmentIsPresent, setDepartmentIsPresent] = useState(true);
    const [selectedFilterOptions, setSelectedFilterOptions] =
        useState<SelectedFilterInterface>(filterOptions);

    const renderOptions = filterTupleMaker(selectedFilterOptions);

    const {
        handleSubmit,
        register,
        formState: { errors },
        watch,
        setValue
    } = useForm();
    const fields = watch();

    // register the form fields so that it will be validated by the useForm hook
    register("formDepartments", { required: true });

    const formSuccessBlock = (
        <SuccessBlock id="gtm-form-submitted">
            <SuccessTitle>
                <SuccessIcon
                    icon={CHECK}
                    size={30}
                    color="var(--color-green-50)"
                />
                Je bericht is verstuurd
                {fields && fields.fullName
                    ? `, ${fields.fullName as string}!`
                    : "!"}
            </SuccessTitle>
            <p>
                {" "}
                Je ontvangt een bevestiging
                {fields && fields.email ? (
                    <b>{` op ${fields.email as string}. `}</b>
                ) : (
                    ". "
                )}
                Wij proberen je binnen twee werkdagen van reactie te voorzien!
            </p>
        </SuccessBlock>
    );

    // eslint-disable-next-line
    const onSubmit: SubmitHandler<any> = async function (
        data: FormInputFields
    ) {
        // eslint-disable-next-line
        const emailBodyHTML = ReactDOMServer.renderToStaticMarkup(
            <div>
                <b>Naam:</b> {data.fullName}
                <br />
                <br />
                <b>Email:</b> {data.email}
                <br />
                <br />
                <b>Bericht:</b> {data.comment || "Geen bericht bijgevoegd"}
                <br />
                <br />
                <b>Filter Opties van {data.fullName}: </b>
                <br />
                <br />
                Departments:{" "}
                {selectedFilterOptions.departmentsFilter.map(department => (
                    <Fragment key={department}>
                        <br />
                        {department}
                    </Fragment>
                ))}
                <br />
                <br />
                Certifications:{" "}
                {selectedFilterOptions.certificationsFilter.map(
                    certification => (
                        <Fragment key={certification}>
                            <br />
                            {getEducationCode(certification)}
                        </Fragment>
                    )
                )}
                <br />
                <br />
                <br />
                {selectedFilterOptions.zzpHoursFilter.length ? (
                    <>
                        ZZP hours:{" "}
                        {selectedFilterOptions.zzpHoursFilter.map(h => (
                            <Fragment key={h}>
                                <br />
                                {h} hours
                            </Fragment>
                        ))}
                    </>
                ) : (
                    <>
                        Minimum hours:{" "}
                        {selectedFilterOptions.minimumHoursFilter}
                        <br />
                        <br />
                        Maximum hours:{" "}
                        {selectedFilterOptions.maximumHoursFilter}
                    </>
                )}
                <br />
                <br />
                <br />
            </div>
        );

        const response = await fetch(`/api/mail`, {
            body: JSON.stringify({
                from: "Noreply <noreply@leadhealthcare.nl>",
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                message: emailBodyHTML,
                subject: `Job alert ${data.fullName}`,
                to: process.env.MAILGUN_RECEIVER
            }),
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            method: "POST"
        });

        if (response.status === 200) {
            setSuccessful(true);
        }
    };

    //useeffect needed to allow any change in filter to affect the alert component but not the other way around
    useEffect(() => {
        setSelectedFilterOptions(filterOptions);
    }, [filterOptions]);

    useEffect(() => {
        setValue("formDepartments", selectedFilterOptions.departmentsFilter);

        if (
            errors.formDepartments &&
            selectedFilterOptions.departmentsFilter.length === 0
        ) {
            setDepartmentIsPresent(false);
        } else {
            setDepartmentIsPresent(true);
        }
    }, [
        errors.formDepartments,
        selectedFilterOptions.departmentsFilter,
        setValue
    ]);

    return (
        <Container id="vacancyAlert">
            <Wrapper>
                <ImageWrapper>
                    <Image
                        src={image.filename}
                        alt={image.alt}
                        width={!isTabletViewOrLarger ? 112 : 302}
                        height={!isTabletViewOrLarger ? 112 : 302}
                    />
                </ImageWrapper>
                <Form>
                    <h5>{title}</h5>
                    <p>{text}</p>
                    {!successful && (
                        <div>
                            <FilterOptionsWrapper>
                                {/* render a filteroption tag for each tuple in renderoptions */}
                                {renderOptions.map(([key, value], index) => {
                                    return (
                                        <FilterOption
                                            key={`${key as string}-${
                                                index + 1
                                            }`}
                                            onClick={() =>
                                                setSelectedFilterOptions(
                                                    filterTupleFilterer(
                                                        key,
                                                        value,
                                                        selectedFilterOptions
                                                    )
                                                )
                                            }
                                        >
                                            {/* check if the value is from a category that needs specific formatting to be rendered and render accordingly */}
                                            {filterOptionslabels([key, value])}
                                            <Icon icon={CLOSE} size={18} />
                                        </FilterOption>
                                    );
                                })}
                                {!departmentIsPresent && (
                                    <StyledErrorMessage>
                                        {t("common:departmentRequired")}
                                    </StyledErrorMessage>
                                )}
                            </FilterOptionsWrapper>
                            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <InputWrapper>
                                    <NameAndEmailWrapper>
                                        <Input
                                            label="Je naam"
                                            placeholder="Vul hier je naam in"
                                            errorMessage={
                                                errors.fullName
                                                    ?.message as FieldError
                                            }
                                            {...register("fullName", {
                                                required: {
                                                    message: "Vul een naam in",
                                                    value: true
                                                }
                                            })}
                                        />
                                        <Input
                                            label="Je e-mailadres"
                                            placeholder="Vul hier je naam in"
                                            errorMessage={
                                                errors.email
                                                    ?.message as FieldError
                                            }
                                            {...register("email", {
                                                pattern: {
                                                    message:
                                                        "Vul een geldig emailadres in",
                                                    value: /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/
                                                },
                                                required: {
                                                    message:
                                                        "Vul een emailadres in",
                                                    value: true
                                                }
                                            })}
                                        />
                                    </NameAndEmailWrapper>
                                    <TextArea
                                        label="Waar ben je naar op zoek:"
                                        placeholder="Vul hier je bericht in"
                                        errorMessage={
                                            errors?.comment?.message as string
                                        }
                                        {...register("comment", {
                                            maxLength: {
                                                message:
                                                    "Maximaal 520 karakters",
                                                value: 520
                                            }
                                        })}
                                    />
                                </InputWrapper>
                                <Button
                                    buttonType="ghost"
                                    size="small"
                                    icon={MAIL}
                                    reversedOrder
                                >
                                    {buttonText}
                                </Button>
                            </form>
                        </div>
                    )}
                    {successful && formSuccessBlock}
                </Form>
            </Wrapper>
        </Container>
    );
}
